import * as React from 'react';
import * as _ from 'underscore';

import { BodyCell, DataGrid, HeaderColumn } from '@tableau/data-grid-3-wrapper';
import { TabStyles } from '@tableau/widgets';

import { CommonStyles } from '../../../common/react/CommonStyles';
import { DeprecatedReactMenu } from '../../../common/react/components/Menu';
import { CreateExplorerEvent } from '../../../common/telemetryEvents/TelemetryEventCreator';
import { ExplorerSortEvent, TelemetryEvents } from '../../../common/telemetryEvents/TelemetryEvents';
import { UserAgentSupport } from '../../../common/utilities/UserAgentSupport';
import { IOrderClause } from '../../../ts/ServerApi/types/IOrderClause';
import { Telemetry } from '../../../ts/services/TelemetryService';
import { SortableHeader } from './SortableHeader';

/////////////////////////////////////
//  Heavily borrowed from common/react/components/SelectableSortKeyDataGridColumn
//  Adjusted for use in react-redux world
/////////////////////////////////////

export interface SelectableSortKeyColumnOptions {
  columnName: string;
  items: SelectableSortKeyColumnItem[];
  defaultDataKey?: string;
  preferredWidth?: number;
}

export interface SelectableSortKeyColumnItem {
  dataKey: string;
  headerText: string;
  order: ReadonlyArray<IOrderClause>;
  cellFormatter?: React.Factory<BodyCell.Props>;
  tooltip?: () => JSX.Element;
}

export interface SelectableSortKeyColumnContext {
  readonly currentSort: ReadonlyArray<IOrderClause>;
  readonly changeSort: (field: string) => void;
  readonly setSortOrder: (order: ReadonlyArray<IOrderClause>) => void;
}

/**
 * Instantiates a column with a selector menu in the header, which allows the
 * user to select different fields to display. Each field can have its own cell formatter.
 *
 * The initially-passed-in "text" in the column spec has no effect; it will be immediately overwritten
 * by the value in headerTextByKey for the currently-selected field.
 *
 * @returns {Utilities.Column}
 * @constructor
 */
export function SelectableSortKeyDataGridColumn(options: SelectableSortKeyColumnOptions,
                                                ctx: SelectableSortKeyColumnContext,
                                                telemetry: Telemetry,
                                                sortable: boolean): DataGrid.Column {

  const currentItem = itemForCurrentSort(options, ctx.currentSort);

  return {
    name: options.columnName,
    dataKey: currentItem.dataKey,
    direction: 'desc',
    headerText: currentItem.headerText,
    sortable: sortable,
    headerFormatter: headerFormatter(options.items, ctx, options.columnName, currentItem, telemetry),
    cellFormatter: currentItem.cellFormatter,
    preferredWidth: options.preferredWidth
  };
}

function itemForCurrentSort(options: SelectableSortKeyColumnOptions, currentSort: ReadonlyArray<IOrderClause>): SelectableSortKeyColumnItem {
  const items = options.items;

  if (currentSort && currentSort.length > 0) {
    for(let clause of currentSort) {
      const applicableItem = _.find(items, i => i.dataKey === clause.field);
      if (applicableItem) {
        return applicableItem;
      }
    }

  }
  return itemForDataKey(options.defaultDataKey, items);
}

function itemForDataKey(dataKey: string, items: SelectableSortKeyColumnItem[]): SelectableSortKeyColumnItem {
  if (!dataKey) return null;
  return _.find(items, item => item.dataKey === dataKey);
}

type DropDownMenuButtonProps = DeprecatedReactMenu.ButtonProps & {
  readonly visible: boolean;
};

class DropDownMenuButton extends React.Component<DropDownMenuButtonProps> {
  public render(): JSX.Element {
    return React.createElement('span', {
      className: 'tb-drop-down-icon',
      style: {
        visibility: this.props.visible ? 'visible' : 'hidden',
      }
    });
  }
}

function headerFormatter(
    items: SelectableSortKeyColumnItem[],
    ctx: SelectableSortKeyColumnContext,
    name: string,
    currentItem: SelectableSortKeyColumnItem,
    telemetry: Telemetry): React.Factory<any> {

  let primaryOrderClause: IOrderClause = { field: undefined, ascending: false };
  if (ctx.currentSort && ctx.currentSort.length > 0) {
    primaryOrderClause = ctx.currentSort[0];
  }

  return React.createFactory(class extends React.Component<HeaderColumn.HeaderFormatterProps, any> {

    public displayName = 'SelectableSortKeyColumnHeaderFormatter';

    state = {
      menuHovered: false
    };

    render() {
      const menuButtonTop = (CommonStyles.Sizes.DataGridHeaderHeight - CommonStyles.Sizes.NormalIconSize) / 2;

      const menu = DeprecatedReactMenu.element({
        ref: 'menu',
        style: {
          fontWeight: 'normal',
          lineHeight: 'normal',
          fontSize: CommonStyles.Sizes.NormalFontSize,

          // vertically center the icon on the right side of the column header
          position: 'absolute',
          top: menuButtonTop,
          right: CommonStyles.Sizes.DefaultSpace
        },
        menuButtonFactory: (buttonProps: DeprecatedReactMenu.ButtonProps) =>
          React.createElement(DropDownMenuButton, {
            visible: buttonProps.menuOpen || this.props.hovered || UserAgentSupport.isTouch()
          }),

        // TODO: refactor this into a common component. Similar code in ActionsMenuColumnCell.ts
        menuPanelFactory: () => {
          const headerColProps: HeaderColumn.Props = this.props;
          const currentKey = headerColProps.col.dataKey;
          const menuItems =
            _.map(items, (item: SelectableSortKeyColumnItem) => {
              const key = item.dataKey;

              const isSelected = key === currentKey;
              const menuItemContent =
                [
                  React.createElement('span', {
                    key: key + '-dot',
                    className: 'tb-padded-right',
                    style: {
                      visibility: isSelected ? 'visible' : 'hidden'
                    }
                  }, '•'),
                  React.createElement('span', { key: key + '-text'}, item.headerText)
                ];

              return React.createElement('div', {
                key: key + '-container',
                className: 'tb-popup-menu-item',
                'data-tb-test-id': key,
                onClick: () => {
                  this.selectKey(key);
                  (<DeprecatedReactMenu>this.refs['menu']).closeMenu();
                }
              } as React.HTMLAttributes<HTMLDivElement>,
              menuItemContent);
            });

          return React.createElement('div', {
            className: 'tb-popup-menu-content tb-padded-small'
          }, menuItems);
        },
        menuPanelStyle: {
          // position drop down 3px below header cell
          top: menuButtonTop + CommonStyles.Sizes.NormalIconSize + CommonStyles.Sizes.DropdownOffset,
          right: -(CommonStyles.Sizes.DefaultSpace),
          left: 'auto'
        }
      });

      const innerHCellStyle = _.extend({}, CommonStyles.Layout.TextEllipsis, {
        marginRight: CommonStyles.Sizes.NormalIconSize + CommonStyles.Sizes.DefaultSpace
      });

      const outerHCellStyle = {
        textDecoration: (this.props.col.sortable && this.props.hovered && !this.state.menuHovered) ? 'underline' : 'none',
        color: this.props.col.sortable && this.isSorted ? TabStyles.Typography.Color.PrimaryDark : TabStyles.Typography.Color.PrimaryLight,
      };

      return React.createElement('div', {
          style: outerHCellStyle,
          onClick: this.handleHeaderClick,
          'data-tb-test-id': 'tb-list-view-header-' + name
        },
        React.createElement('div', {
            style: innerHCellStyle
          },
          SortableHeader.ArrowIcon({sorted: this.props.col.sortable && this.isSorted, ascending:this.ascending, supportsAscDesc: true}),
          (<HeaderColumn.Props>this.props).col.headerText,
          currentItem.tooltip && currentItem.tooltip()
        ),
        React.createElement('div', {
          // stop propagation to prevent header cell from sorting.
          onClick: (event: React.MouseEvent<HTMLDivElement>) => event.stopPropagation(),
          onMouseOver: () => this.setState({ menuHovered: true }),
          onMouseOut: () => this.setState({ menuHovered: false })
        }, menu)
      );
    }

    private handleHeaderClick = () => {
      if (!this.props.col.sortable) {
        return;
      }

      ctx.changeSort(this.props.col.dataKey);

      // if the primaryOrderClause is for this column, then handleClick is reversing the order. If it
      // is not, it means the user first clicked the header and we are using the default direction value.
      const asc = (this.props.col.dataKey === primaryOrderClause.field)
      ? !primaryOrderClause.ascending
      : (this.props.col.direction === 'asc');

      sendTelemetryEvent(this.props.col.dataKey, asc);
    }

    private selectKey(key: string) {
      if (key !== this.props.col.dataKey) {
        const newOrder = itemForDataKey(key, items).order;
        ctx.setSortOrder(newOrder);

        sendTelemetryEvent(newOrder[0].field, newOrder[0].ascending);
      }
    }

    private get isSorted(): boolean {
      return primaryOrderClause.field === this.props.col.dataKey;
    }

    private get ascending(): boolean {
      return primaryOrderClause.ascending;
    }
  });

  function sendTelemetryEvent(sortField: string, ascending: boolean) {
    const event = CreateExplorerEvent<ExplorerSortEvent>({
      type: TelemetryEvents.ExplorerSort,
      sortField: sortField,
      ascending: ascending,
      eventSource: 'column'
    });
    telemetry.sendEvent(event);
  }
}
